import AddNewProfileIcon from '@assets/images/icons-filled/add-new-profile.svg';
import { useEntityMenuColors } from '@containers/Layout/AppBar/useEntityMenuColors';
import {
  FeatureFlagTypes,
  OrganizationUserAccessTypes,
  OrganizationUserEntityRelationTypes,
} from '@generated/graphql';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { MemberTag } from '@pages/HouseholdDetail/memberTag';
import {
  type ActiveEntityProps,
  UserContext,
} from '@providers/userContextProvider';
import { entityInitials, entityName } from '@utils/common';
import filterHouseholdsWithAccess from '@utils/filterHouseholdsWithAccess';
import {
  Avatar,
  Box,
  Divider,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from 'hephaestus/web';
import {
  CheckRounded as CheckRoundedIcon,
  CloseRounded as CloseRoundedIcon,
} from 'hephaestus/webIcons';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useOrganizationFeatureFlag } from '../../../hooks/useOrganizationFeatureFlag';
import type { IUserEntity } from './viewAllProfilesDialog.types';

export const ViewAllProfilesDialog = ({
  open,
  onAddNewProfile,
  onViewDetail,
  onCancel,
}: {
  open: boolean;
  onAddNewProfile: () => void;
  onViewDetail: () => void;
  onCancel: () => void;
}) => {
  const navigate = useNavigate();
  const colors = useEntityMenuColors();
  const { t } = useTranslation(['components', 'entityMenu', 'navMenu']);
  const { userContext, activeEntity, setActiveEntity, setActiveHousehold } =
    useContext(UserContext);
  const allowAddingNewProfile = useOrganizationFeatureFlag(
    FeatureFlagTypes.AllowAddingANewProfile,
  );

  // Falls back to '' so the localeCompare sorts cannot throw on an unnamed entity.
  const profileName = useCallback(
    (entity?: ActiveEntityProps) =>
      entityName(entity) || entity?.primaryEmail || '',
    [],
  );
  const profileInitials = useCallback(
    (entity?: ActiveEntityProps) => entityInitials(entity),
    [],
  );

  const getGroupedEntitiesSortedByName = () => {
    const userEntities: IUserEntity[] =
      (userContext.accessType === OrganizationUserAccessTypes.RepCode
        ? userContext?.repCodeEntities
        : userContext?.entities) ?? [];
    const entities = userEntities?.filter((x) => !x.entity.affiliateOnly) ?? [];

    const individualEntities = entities.filter(
      (item) => item.entity.type === 'INDIVIDUAL',
    );
    const nonIndividualEntities = entities.filter(
      (item) => item.entity.type !== 'INDIVIDUAL',
    );

    individualEntities.sort((a, b) =>
      profileName(a.entity).localeCompare(profileName(b.entity)),
    );
    nonIndividualEntities.sort((a, b) =>
      profileName(a.entity).localeCompare(profileName(b.entity)),
    );

    return { individualEntities, nonIndividualEntities };
  };

  const handleEntitySelect = (item: IUserEntity) => {
    const households = filterHouseholdsWithAccess(
      item.entity?.households || [],
      item.entity?.id,
      userContext,
    );
    const isHouseholdDefaultView =
      !!userContext.role?.makeHouseholdDefaultView && households.length > 0;
    if (isHouseholdDefaultView) {
      const household = households[0];
      setActiveHousehold(household);
      navigate(`/households/${household?.id}/clients/${item.entity.id}`);
    }
    setActiveEntity(item.entity);
    onCancel();
  };

  const isAccessGranted =
    userContext.accessType === OrganizationUserAccessTypes.RepCode ||
    !!userContext.entities?.some(
      (en) =>
        en.entity?.id === activeEntity?.id &&
        en.relation !== OrganizationUserEntityRelationTypes.Owner,
    );

  const { individualEntities, nonIndividualEntities } =
    getGroupedEntitiesSortedByName();

  return (
    <Dialog open={open}>
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='headingLarge'>
          {t('components:viewAllProfilesDialog.switchProfile')}
        </Typography>
        <IconButton aria-label='close' onClick={onCancel}>
          <CloseRoundedIcon fontSize='inherit' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: 2.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Box component='span' sx={{ mr: 1.5 }}>
              <Avatar
                selected
                size='small'
                sx={{ color: colors[1].color, bgcolor: colors[1].colorsInner }}
              >
                {profileInitials(activeEntity)}
              </Avatar>
            </Box>

            <Box sx={{ maxWidth: '400px' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='bodyLarge'
                  sx={[{ mr: 1, fontWeight: 600 }, profileNameSx]}
                >
                  {profileName(activeEntity)}
                </Typography>
                <CheckRoundedIcon style={{ fontSize: '14px' }} />
              </Box>
              {isAccessGranted && (
                <MemberTag
                  sequenceNumber={1}
                  name={t('entityMenu:accessGranted')}
                />
              )}
            </Box>
          </Box>
          <Typography
            variant='bodyMedium'
            onClick={onViewDetail}
            sx={{
              cursor: 'pointer',
              display: '-webkit-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical',
              fontWeight: 500,
              mr: 1,
            }}
          >
            {t('components:viewAllProfilesDialog.viewDetails')}
          </Typography>
        </Box>
        {individualEntities.length > 0 && (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {individualEntities.map((item, index) => (
              <ProfileGridItem
                key={item.entity.id}
                item={item}
                index={index}
                onClick={handleEntitySelect}
              />
            ))}
          </Grid>
        )}
        {individualEntities.length > 0 && nonIndividualEntities.length > 0 && (
          <Divider sx={{ marginBottom: 1 }} />
        )}
        {nonIndividualEntities.length > 0 && (
          <>
            <Typography
              variant='bodyMedium'
              color='textSecondary'
              sx={{ py: 1, fontWeight: 500, fontSize: '12px' }}
            >
              {t('navMenu:menu.nonIndividualClients').toUpperCase()}
            </Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {nonIndividualEntities.map((item, index) => (
                <ProfileGridItem
                  key={item.entity.id}
                  item={item}
                  index={index}
                  onClick={handleEntitySelect}
                />
              ))}
            </Grid>
          </>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 2.5,
          }}
        >
          {allowAddingNewProfile && (
            <Box
              onClick={onAddNewProfile}
              style={{ cursor: 'pointer' }}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                component='img'
                src={AddNewProfileIcon}
                alt='view-profile-icon'
                sx={{ mr: 1.5 }}
              />

              <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                {t('components:viewAllProfilesDialog.addNewEntity')}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const ProfileGridItem = ({
  item,
  index,
  onClick,
}: {
  item: IUserEntity;
  index: number;
  onClick: (item: IUserEntity) => void;
}) => {
  const theme = useTheme();
  const colors = useEntityMenuColors();
  const { t } = useTranslation(['components', 'entityMenu']);

  const profileName = useCallback(
    (entity?: ActiveEntityProps) => entityName(entity) || entity?.primaryEmail,
    [],
  );
  const profileInitials = useCallback(
    (entity?: ActiveEntityProps) => entityInitials(entity),
    [],
  );

  const isAccessGranted =
    item.relation !== OrganizationUserEntityRelationTypes.Owner;
  const colorIndex = index % colors.length;

  return (
    <Grid key={item.entity.id} data-testid={`switch-profile-${index}`} size={6}>
      <Box
        onClick={() => onClick(item)}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          paddingLeft: 1.5,
          borderRadius: theme.borderRadius.lg,
          cursor: 'pointer',
          height: '48px',
          width: '256px',
          border: `1px solid ${theme.color.outlineVariant}`,
        }}
      >
        <Box component='span' sx={{ mr: 1.5 }}>
          <Avatar
            selected
            size='small'
            sx={{
              color: colors[colorIndex].color,
              bgcolor: colors[colorIndex].colorsInner,
            }}
          >
            {profileInitials(item.entity)}
          </Avatar>
        </Box>
        <Box sx={{ maxWidth: '200px' }}>
          <Typography
            variant='bodyMedium'
            align='left'
            sx={[{ fontWeight: 500 }, profileNameSx]}
          >
            {profileName(item.entity)}
          </Typography>
          {isAccessGranted && (
            <MemberTag
              sequenceNumber={colorIndex === 4 ? 6 : colorIndex}
              name={t('entityMenu:accessGranted')}
            />
          )}
        </Box>
      </Box>
    </Grid>
  );
};

const profileNameSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
};
